import * as i0 from '@angular/core';
import { Injectable, Directive, Host, Input, HostListener, Component, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { checkInterval } from '@js-dot/core';
import { DateDigitUnits } from '@js-dot/core/date';
import { renderDate } from '@js-dot/ui';
import { DOT_TRANSLATE_SERVICE } from '@ng-dot/text';
import * as i2 from '@ng-dot/ui/tip';
import { DotTipDirective } from '@ng-dot/ui/tip';
import { isObservable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as i1 from '@ng-dot/core';
const {
  isArray: IS_ARRAY
} = Array;
const {
  round: ROUND,
  abs: ABS,
  floor: FLOOR,
  ceil: CEIL
} = Math;
const NOW_ENDS = 3;
class DotRelativeTimeService {
  disable;
  disableRenderer = renderDate;
  static ɵfac = function DotRelativeTimeService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotRelativeTimeService)();
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DotRelativeTimeService,
    factory: DotRelativeTimeService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotRelativeTimeService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], null, null);
})();
/**
 * Date Object to Relative time string.
 *
 * @description
 * in English:
 *
 * - 2 days ago
 * - yesterday
 * - 1 hour ago
 * - now
 * - in 1 hour
 * - tomorrow
 * - in 2 days
 */
class DotRelativeTimeDirective {
  _service;
  _core;
  _tip;
  /**
   * Set Date from selector attribute
   * @param date
   */
  set fromSelector(date) {
    if (!!date) {
      this.date = date;
    }
  }
  /**
   * Set Date
   * @param date
   */
  set date(date) {
    this._date = date instanceof Date ? date : date === true ? new Date() : date === false ? null : new Date(date);
    this.setCount();
  }
  /**
   * Set Date as Now when pushed data.
   * @param date
   */
  set signal(date) {
    const {
      _destroy$
    } = this;
    _destroy$.next();
    if (isObservable(date)) {
      date.pipe(takeUntil(_destroy$)).subscribe(() => {
        this.date = new Date();
      });
    } else {
      this.date = new Date();
    }
  }
  /**
   * Set Date from InnerHTML property
   * @param date
   */
  set innerHTML(date) {
    this.date = date;
  }
  /**
   * Set Date from textContent property
   * @param date
   */
  set textContent(date) {
    this.date = date;
  }
  /**
   * Set display language
   * @default Runtime environment. refers to DotTranslateModule, If available.
   */
  locales;
  _destroy$ = new Subject();
  _formatter;
  _date;
  _host;
  _isTimeElement;
  _pastSeconds;
  _delay = 1;
  _timer;
  _hidden;
  _selfSetContent = this.setContent.bind(this);
  constructor(_service, _core, _tip,
  // _style: DotRelativeTimeStyleService,
  host) {
    this._service = _service;
    this._core = _core;
    this._tip = _tip;
    this._host = host.nativeElement;
    this._isTimeElement = this._host.tagName === 'TIME';
  }
  ngOnInit() {
    const {
      _core
    } = this;
    if (!this.locales) {
      this.locales = _core.includes(DOT_TRANSLATE_SERVICE) ? _core.inject(DOT_TRANSLATE_SERVICE).language : undefined;
    }
    this._formatter = getFormatter(this.locales);
    this.setCount();
  }
  ngOnDestroy() {
    clearInterval(this._timer);
  }
  onVisibilityChange(event) {
    if (event.target.hidden) {
      this._hidden = true;
      this._timer = checkInterval(this._timer);
    } else {
      this._hidden = false;
      this.setContent();
    }
  }
  setTimer(delay) {
    this._delay = delay;
    this._timer = checkInterval(this._timer, this._selfSetContent, delay * 1000);
  }
  #disabled() {
    const {
      _service
    } = this;
    if (_service.disable) {
      const {
        _date
      } = this;
      this._host.innerHTML = _date ? _service.disableRenderer(_date) : '';
      return true;
    }
    return false;
  }
  setCount() {
    if (this.#disabled()) {
      return;
    }
    const {
      _date,
      _tip,
      _isTimeElement,
      _host
    } = this;
    if (!this._formatter || !_date || !(_date instanceof Date)) {
      _tip.tip = null;
      if (_isTimeElement) {
        _host.dateTime = null;
      }
      return;
    }
    _tip.tip = _date.toLocaleString(this.locales);
    if (_isTimeElement) {
      _host.dateTime = _date.toISOString();
    }
    this._pastSeconds = ROUND((_date.getTime() - Date.now()) / 1000);
    this.setContent();
  }
  setContent() {
    if (this._hidden || this.#disabled()) {
      return;
    }
    const {
      _formatter,
      _host,
      _date
    } = this;
    let value = this._pastSeconds;
    let absoluteValue = ABS(value);
    let unit;
    if (value < 0 && absoluteValue < NOW_ENDS) {
      this.setTimer(NOW_ENDS - absoluteValue);
      value = 0;
      unit = 'second';
    } else if (absoluteValue < 60) {
      this.setTimer(1);
      unit = 'second';
    } else if (absoluteValue < 3600) {
      this.setTimer(60);
      value = FLOOR(value / 60);
      unit = 'minute';
    } else if (absoluteValue < 86400) {
      this.setTimer(600);
      value = FLOOR(value / 3600);
      unit = 'hour';
    } else if (absoluteValue < 2419200) {
      // over 28 days
      this.setTimer(3600);
      value = value > 0 ? FLOOR(value / 86400) : CEIL(value / 86400);
      unit = 'day';
    } else {
      const now = new DateDigitUnits();
      const date = new DateDigitUnits(_date);
      let month = FLOOR(date.m - ((now.y - date.y) * 12 + now.m));
      if (ABS(month) > 11) {
        value = FLOOR(month / 12);
        unit = 'year';
      } else {
        value = month;
        unit = 'month';
      }
      this.setTimer(86400);
    }
    this._pastSeconds -= this._delay;
    _host.textContent = _formatter.format(value, unit);
  }
  static ɵfac = function DotRelativeTimeDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotRelativeTimeDirective)(i0.ɵɵdirectiveInject(DotRelativeTimeService), i0.ɵɵdirectiveInject(i1.DotCoreService), i0.ɵɵdirectiveInject(i2.DotTipDirective, 1), i0.ɵɵdirectiveInject(i0.ElementRef));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: DotRelativeTimeDirective,
    selectors: [["", "dotRelativeTime", ""]],
    hostAttrs: [1, "dot-relative-time"],
    hostBindings: function DotRelativeTimeDirective_HostBindings(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵlistener("visibilitychange", function DotRelativeTimeDirective_visibilitychange_HostBindingHandler($event) {
          return ctx.onVisibilityChange($event);
        }, false, i0.ɵɵresolveDocument);
      }
    },
    inputs: {
      fromSelector: [0, "dotRelativeTime", "fromSelector"],
      date: "date",
      signal: "signal",
      innerHTML: "innerHTML",
      textContent: "textContent",
      locales: "locales"
    },
    features: [i0.ɵɵHostDirectivesFeature([i2.DotTipDirective])]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotRelativeTimeDirective, [{
    type: Directive,
    args: [{
      standalone: true,
      selector: '[dotRelativeTime]',
      host: {
        class: 'dot-relative-time'
      },
      hostDirectives: [{
        directive: DotTipDirective
      }]
    }]
  }], () => [{
    type: DotRelativeTimeService
  }, {
    type: i1.DotCoreService
  }, {
    type: i2.DotTipDirective,
    decorators: [{
      type: Host
    }]
  }, {
    type: i0.ElementRef
  }], {
    fromSelector: [{
      type: Input,
      args: ['dotRelativeTime']
    }],
    date: [{
      type: Input
    }],
    signal: [{
      type: Input
    }],
    innerHTML: [{
      type: Input
    }],
    textContent: [{
      type: Input
    }],
    locales: [{
      type: Input
    }],
    onVisibilityChange: [{
      type: HostListener,
      args: ['document:visibilitychange', ['$event']]
    }]
  });
})();
class DotRelativeTimeComponent {
  static ɵfac = function DotRelativeTimeComponent_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotRelativeTimeComponent)();
  };
  static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: DotRelativeTimeComponent,
    selectors: [["dot-relative-time"]],
    features: [i0.ɵɵHostDirectivesFeature([{
      directive: DotRelativeTimeDirective,
      inputs: ["date", "date", "signal", "signal", "textContent", "textContent", "innerHTML", "innerHTML"]
    }])],
    decls: 0,
    vars: 0,
    template: function DotRelativeTimeComponent_Template(rf, ctx) {},
    encapsulation: 2,
    changeDetection: 0
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotRelativeTimeComponent, [{
    type: Component,
    args: [{
      standalone: true,
      selector: 'dot-relative-time',
      template: ``,
      encapsulation: ViewEncapsulation.None,
      changeDetection: ChangeDetectionStrategy.OnPush,
      hostDirectives: [{
        directive: DotRelativeTimeDirective,
        inputs: ['date', 'signal', 'textContent', 'innerHTML']
      }]
    }]
  }], null, null);
})();
function getFormatter(locales) {
  const joinedLocales = (IS_ARRAY(locales) ? locales : [locales]).join('|');
  return getFormatter[joinedLocales] || (getFormatter[joinedLocales] = new Intl.RelativeTimeFormat(locales, {
    numeric: 'auto'
  }));
}

/**
 * Generated bundle index. Do not edit.
 */

export { DotRelativeTimeComponent, DotRelativeTimeDirective, DotRelativeTimeService };
